
import React from 'react';
import { Search, Calendar, Building, User, Tag, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { useBrands } from '@/hooks/useBrands';

export interface ContactFilters {
  name: string;
  email: string;
  companyName: string;
  designation: string;
  contactType: string;
  linkedBrandIds: string[];
  createdAtFrom: string;
  createdAtTo: string;
}

interface ContactsFilterProps {
  filters: ContactFilters;
  onFiltersChange: (filters: ContactFilters) => void;
  onClearFilters: () => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const contactTypes = ['Marketing', 'Finance', 'Legal', 'Logistics', 'Vendor', 'Other'];

const ContactsFilter: React.FC<ContactsFilterProps> = ({
  filters,
  onFiltersChange,
  onClearFilters,
  isOpen,
  onOpenChange,
}) => {
  const { data: brands = [] } = useBrands();

  const handleFilterChange = (field: keyof ContactFilters, value: string | string[]) => {
    onFiltersChange({
      ...filters,
      [field]: value,
    });
  };

  const handleBrandToggle = (brandId: string) => {
    const newBrandIds = filters.linkedBrandIds.includes(brandId)
      ? filters.linkedBrandIds.filter(id => id !== brandId)
      : [...filters.linkedBrandIds, brandId];
    
    handleFilterChange('linkedBrandIds', newBrandIds);
  };

  const handleContactTypeChange = (value: string) => {
    handleFilterChange('contactType', value === 'clear' ? '' : value);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent className="w-[400px] sm:w-[540px] flex flex-col">
        <SheetHeader className="pb-4">
          <SheetTitle className="text-xl font-semibold">Filter Contacts</SheetTitle>
        </SheetHeader>
        
        <div className="flex-1 overflow-y-auto pb-20">
          <div className="space-y-8">
            {/* Basic Information Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <h3 className="font-medium text-sm text-foreground">Basic Information</h3>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pl-6">
                <div className="space-y-2">
                  <Label htmlFor="name-filter" className="text-sm font-medium">Name</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="name-filter"
                      placeholder="Search by name..."
                      value={filters.name}
                      onChange={(e) => handleFilterChange('name', e.target.value)}
                      className="pl-10 bg-background border-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email-filter" className="text-sm font-medium">Email</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email-filter"
                      placeholder="Search by email..."
                      value={filters.email}
                      onChange={(e) => handleFilterChange('email', e.target.value)}
                      className="pl-10 bg-background border-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company-filter" className="text-sm font-medium">Company</Label>
                  <div className="relative">
                    <Building className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="company-filter"
                      placeholder="Search by company..."
                      value={filters.companyName}
                      onChange={(e) => handleFilterChange('companyName', e.target.value)}
                      className="pl-10 bg-background border-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="designation-filter" className="text-sm font-medium">Designation</Label>
                  <div className="relative">
                    <Tag className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="designation-filter"
                      placeholder="Search by designation..."
                      value={filters.designation}
                      onChange={(e) => handleFilterChange('designation', e.target.value)}
                      className="pl-10 bg-background border-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Type & Associations Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <h3 className="font-medium text-sm text-foreground">Type & Associations</h3>
              </div>
              
              <div className="space-y-4 pl-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Contact Type</Label>
                  <Select
                    value={filters.contactType || 'clear'}
                    onValueChange={handleContactTypeChange}
                  >
                    <SelectTrigger className="bg-background border-input">
                      <SelectValue placeholder="Select contact type" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="clear">All Types</SelectItem>
                      {contactTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Linked Brands</Label>
                  <div className="bg-muted/30 border border-border rounded-md p-3">
                    <div className="grid grid-cols-1 gap-3 max-h-32 overflow-y-auto">
                      {brands.map((brand) => (
                        <label key={brand.id} className="flex items-center space-x-3 text-sm cursor-pointer hover:bg-muted/50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={filters.linkedBrandIds.includes(brand.id)}
                            onChange={() => handleBrandToggle(brand.id)}
                            className="rounded border-border text-primary focus:ring-primary"
                          />
                          <span className="truncate text-foreground">{brand.name}</span>
                        </label>
                      ))}
                      {brands.length === 0 && (
                        <span className="text-sm text-muted-foreground">No brands available</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Date Range Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <h3 className="font-medium text-sm text-foreground">Created Date</h3>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pl-6">
                <div className="space-y-2">
                  <Label htmlFor="date-from" className="text-sm font-medium">From Date</Label>
                  <Input
                    id="date-from"
                    type="date"
                    value={filters.createdAtFrom}
                    onChange={(e) => handleFilterChange('createdAtFrom', e.target.value)}
                    className="bg-background border-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date-to" className="text-sm font-medium">To Date</Label>
                  <Input
                    id="date-to"
                    type="date"
                    value={filters.createdAtTo}
                    onChange={(e) => handleFilterChange('createdAtTo', e.target.value)}
                    className="bg-background border-input"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Action Buttons */}
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-background border-t border-border">
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={onClearFilters}
              className="flex-1 border-border hover:bg-muted"
            >
              Clear Filters
            </Button>
            <Button 
              onClick={() => onOpenChange(false)}
              className="flex-1 bg-primary hover:bg-primary/90"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ContactsFilter;
